import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  Typography,
  Autocomplete,
  CircularProgress
} from '@mui/material';

const PersonDialog = ({
  open,
  onClose,
  onSave,
  person,
  personTypes,
  locations,
  saving,
  loadingTypes,
  loadingLocations
}) => {
  const [form, setForm] = useState({
    friendlyName: '',
    personType: '',
    personLocation: '',
    data: {}
  });
  const [errors, setErrors] = useState({});
  const [selectedType, setSelectedType] = useState(null);

  useEffect(() => {
    if (person) {
      setForm({
        friendlyName: person.friendlyName || '',
        personType: person.personType || '',
        personLocation: person.personLocation || '',
        data: person.data || {}
      });
      // Find the selected type
      const type = personTypes.find(t => t.id === person.personType);
      setSelectedType(type);
    } else {
      setForm({
        friendlyName: '',
        personType: '',
        personLocation: '',
        data: {}
      });
      setSelectedType(null);
    }
    setErrors({});
  }, [person, personTypes, open]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleTypeChange = (typeId) => {
    const type = personTypes.find(t => t.id === typeId);
    setSelectedType(type);
    handleChange('personType', typeId);

    // Initialize data fields based on type's dataModel, preserving system fields
    if (type?.dataModel) {
      const newData = {};
      if (form.data.gameLanguage) newData.gameLanguage = form.data.gameLanguage;
      type.dataModel.forEach(field => {
        newData[field.key] = form.data[field.key] || '';
      });
      setForm(prev => ({ ...prev, data: newData }));
    }
  };

  const handleDataChange = (key, value) => {
    setForm(prev => ({
      ...prev,
      data: { ...prev.data, [key]: value }
    }));
    if (errors[`data_${key}`]) {
      setErrors(prev => ({ ...prev, [`data_${key}`]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!form.friendlyName.trim()) {
      newErrors.friendlyName = 'Name is required';
    }
    if (!form.personType) {
      newErrors.personType = 'Person type is required';
    }

    // Validate required custom fields
    if (selectedType?.dataModel) {
      selectedType.dataModel.forEach(field => {
        if (field.required && !form.data[field.key]) {
          newErrors[`data_${field.key}`] = `${field.label} is required`;
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSave({
        friendlyName: form.friendlyName.trim(),
        personType: form.personType,
        personLocation: form.personLocation || null,
        data: form.data
      });
    }
  };

  const renderCustomField = (field) => {
    const value = form.data[field.key] || '';
    const error = errors[`data_${field.key}`];

    switch (field.type) {
      case 'select':
        return (
          <FormControl fullWidth size="small" error={!!error} key={field.key}>
            <InputLabel>{field.label}{field.required ? ' *' : ''}</InputLabel>
            <Select
              value={value}
              onChange={(e) => handleDataChange(field.key, e.target.value)}
              label={`${field.label}${field.required ? ' *' : ''}`}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {(field.options || []).map(option => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            {error && (
              <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>
                {error}
              </Typography>
            )}
          </FormControl>
        );

      case 'boolean':
        return (
          <FormControlLabel
            key={field.key}
            control={
              <Switch
                checked={!!value}
                onChange={(e) => handleDataChange(field.key, e.target.checked)}
              />
            }
            label={field.label}
          />
        );

      case 'textarea':
        return (
          <TextField
            key={field.key}
            fullWidth
            multiline
            rows={3}
            label={`${field.label}${field.required ? ' *' : ''}`}
            value={value}
            onChange={(e) => handleDataChange(field.key, e.target.value)}
            error={!!error}
            helperText={error}
            size="small"
          />
        );

      case 'number':
        return (
          <TextField
            key={field.key}
            fullWidth
            type="number"
            label={`${field.label}${field.required ? ' *' : ''}`}
            value={value}
            onChange={(e) => handleDataChange(field.key, e.target.value)}
            error={!!error}
            helperText={error}
            size="small"
          />
        );

      case 'date':
        return (
          <TextField
            key={field.key}
            fullWidth
            type="date"
            label={`${field.label}${field.required ? ' *' : ''}`}
            value={value}
            onChange={(e) => handleDataChange(field.key, e.target.value)}
            error={!!error}
            helperText={error}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        );

      case 'email':
        return (
          <TextField
            key={field.key}
            fullWidth
            type="email"
            label={`${field.label}${field.required ? ' *' : ''}`}
            value={value}
            onChange={(e) => handleDataChange(field.key, e.target.value)}
            error={!!error}
            helperText={error}
            size="small"
          />
        );

      case 'phone':
        return (
          <TextField
            key={field.key}
            fullWidth
            type="tel"
            label={`${field.label}${field.required ? ' *' : ''}`}
            value={value}
            onChange={(e) => handleDataChange(field.key, e.target.value)}
            error={!!error}
            helperText={error}
            size="small"
          />
        );

      default: // text
        return (
          <TextField
            key={field.key}
            fullWidth
            label={`${field.label}${field.required ? ' *' : ''}`}
            value={value}
            onChange={(e) => handleDataChange(field.key, e.target.value)}
            error={!!error}
            helperText={error}
            size="small"
          />
        );
    }
  };

  const isEditing = !!person;

  // Find selected location for Autocomplete
  const selectedLocation = locations.find(l => l.id === form.personLocation) || null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditing ? 'Edit Person' : 'Add Person'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Basic Fields */}
          <TextField
            fullWidth
            label="Name"
            value={form.friendlyName}
            onChange={(e) => handleChange('friendlyName', e.target.value)}
            error={!!errors.friendlyName}
            helperText={errors.friendlyName}
            required
          />

          <FormControl fullWidth error={!!errors.personType} required>
            <InputLabel>Person Type</InputLabel>
            <Select
              value={form.personType}
              onChange={(e) => handleTypeChange(e.target.value)}
              label="Person Type"
              disabled={loadingTypes}
            >
              {loadingTypes ? (
                <MenuItem disabled>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Loading...
                </MenuItem>
              ) : (
                personTypes.map(type => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.friendlyName}
                  </MenuItem>
                ))
              )}
            </Select>
            {errors.personType && (
              <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>
                {errors.personType}
              </Typography>
            )}
          </FormControl>

          <Autocomplete
            options={locations}
            getOptionLabel={(option) => option.friendlyName || ''}
            value={selectedLocation}
            onChange={(_, newValue) => handleChange('personLocation', newValue?.id || '')}
            loading={loadingLocations}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Location"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingLocations && <CircularProgress size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
          />

          {/* Game Language */}
          <FormControl fullWidth size="small">
            <InputLabel>Game Language</InputLabel>
            <Select
              value={form.data.gameLanguage || 'en'}
              onChange={(e) => handleDataChange('gameLanguage', e.target.value)}
              label="Game Language"
            >
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="nl">Nederlands</MenuItem>
              <MenuItem value="fr">Fran&#231;ais</MenuItem>
              <MenuItem value="de">Deutsch</MenuItem>
            </Select>
          </FormControl>

          {/* Custom Fields from PersonType */}
          {selectedType?.dataModel && selectedType.dataModel.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                {selectedType.friendlyName} Fields
              </Typography>
              {selectedType.dataModel.map(renderCustomField)}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
        >
          {saving ? 'Saving...' : isEditing ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PersonDialog;
